import { useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Search, Building2, User } from "lucide-react";
import { useCreateVisitor, useVisitorSearch, type VisitorSuggestion } from "@/hooks/useVisitors";
import { useActiveDestinations } from "@/hooks/useDestinations";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import WebcamCapture from "./WebcamCapture";
import { format } from "date-fns";

interface NovoRegistroAcessoProps {
  onSuccess: () => void;
}

const NovoRegistroAcesso = ({ onSuccess }: NovoRegistroAcessoProps) => {
  const { profile } = useAuth();
  const createVisitor = useCreateVisitor();
  const { data: destinations = [] } = useActiveDestinations();
  const [name, setName] = useState("");
  const [rg, setRg] = useState("");
  const [cpf, setCpf] = useState("");
  const [destino, setDestino] = useState<string | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastVisitInfo, setLastVisitInfo] = useState<string | null>(null);

  // Autocomplete: debounce e visibilidade do dropdown
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data: suggestions = [], isFetching: isSearching } = useVisitorSearch(
    debouncedTerm,
    profile?.condominium_id || null
  );

  const showSuggestions =
    suggestionsOpen &&
    debouncedTerm.length >= 3 &&
    !justSelectedRef.current;

  const handleCapture = (blob: Blob) => {
    setPhotoBlob(blob);
    setPhotoPreview(URL.createObjectURL(blob));
  };

  const updateSearchFromField = useCallback(
    (field: "name" | "rg" | "cpf", value: string) => {
      justSelectedRef.current = false;
      setSearchTerm(value);
      setSuggestionsOpen(value.trim().length >= 3);
    },
    []
  );

  const parseDocumentString = (doc: string | null) => {
    if (!doc) return { rg: "", cpf: "" };
    const rgMatch = doc.match(/RG:\s*([^|]+)/i);
    const cpfMatch = doc.match(/CPF:\s*([^|]+)/i);
    return {
      rg: rgMatch?.[1].trim() ?? "",
      cpf: cpfMatch?.[1].trim() ?? "",
    };
  };

  const handleSelectSuggestion = (s: VisitorSuggestion) => {
    justSelectedRef.current = true;
    setSuggestionsOpen(false);
    setName(s.name || "");
    const { rg: parsedRg, cpf: parsedCpf } = parseDocumentString(s.document);
    // Se nao houver prefixo "RG:"/"CPF:", colocamos no campo CPF se for so digito
    if (!parsedRg && !parsedCpf && s.document) {
      const onlyDigits = s.document.replace(/\D/g, "");
      if (onlyDigits.length === 11) setCpf(s.document);
      else setRg(s.document);
    } else {
      setRg(parsedRg);
      setCpf(parsedCpf);
    }
    if (s.photo_url) setPhotoPreview(s.photo_url);
    if (s.notes) {
      const matchedDest = destinations.find(d => d.name === s.notes);
      if (matchedDest) setDestino(matchedDest.name);
    }
    const visitDate = s.created_at ? format(new Date(s.created_at), "dd/MM/yyyy HH:mm") : "";
    setLastVisitInfo(`Última visita: ${visitDate}${s.notes ? ` • Destino: ${s.notes}` : ""}`);
  };

  const resetForm = () => {
    setName("");
    setRg("");
    setCpf("");
    setDestino(null);
    setPhotoBlob(null);
    setPhotoPreview(null);
    setLastVisitInfo(null);
    setSearchTerm("");
    setDebouncedTerm("");
    setSuggestionsOpen(false);
    justSelectedRef.current = false;
  };

  const handleSubmit = async () => {
    if (!name.trim() || !destino) return;
    setIsSubmitting(true);

    try {
      let photoUrl: string | null = null;

      if (photoBlob) {
        const fileName = `access_${Date.now()}.jpg`;
        const { data, error } = await supabase.storage.from("staff-photos").upload(fileName, photoBlob);
        if (error) throw error;
        const { data: urlData } = supabase.storage.from("staff-photos").getPublicUrl(data.path);
        photoUrl = urlData.publicUrl;
      }

      const docParts = [rg && `RG: ${rg}`, cpf && `CPF: ${cpf}`].filter(Boolean).join(" | ");

      await createVisitor.mutateAsync({
        name: name.trim(),
        document: docParts || null,
        notes: destino,
        photo_url: photoUrl || (photoPreview?.startsWith("http") ? photoPreview : null),
        visitor_type: "casual",
        entry_time: new Date().toISOString(),
        visit_date: new Date().toISOString().split("T")[0],
        status: "authorized",
        authorized_by: profile?.id || null,
        unit_id: null,
        exit_time: null,
        vehicle_plate: null,
      });

      resetForm();
      onSuccess();
    } catch (err) {
      console.error("Erro ao registrar acesso:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = name.trim().length >= 2 && destino;
  const protocol = `REG-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999)).padStart(4, "0")}`;

  const formatCpfMask = (val: string) => {
    const d = val.replace(/\D/g, "");
    if (d.length !== 11) return val;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Form */}
      <div className="lg:col-span-2 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Identificação do Visitante</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="relative">
              <Label className="text-xs">Nome Completo *</Label>
              <Input
                value={name}
                onChange={e => {
                  setName(e.target.value);
                  updateSearchFromField("name", e.target.value);
                }}
                onFocus={() => {
                  if (name.trim().length >= 3) {
                    
                    setSearchTerm(name);
                    setSuggestionsOpen(true);
                  }
                }}
                placeholder="Nome do visitante"
                className="h-9 text-sm"
                autoComplete="off"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">RG</Label>
                <div className="relative">
                  <Input
                    value={rg}
                    onChange={e => {
                      setRg(e.target.value);
                      updateSearchFromField("rg", e.target.value);
                    }}
                    onFocus={() => {
                      if (rg.trim().length >= 3) {
                        
                        setSearchTerm(rg);
                        setSuggestionsOpen(true);
                      }
                    }}
                    placeholder="00.000.000-0"
                    className="h-9 text-sm pr-8"
                    autoComplete="off"
                  />
                  {isSearching && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-muted-foreground" />}
                </div>
              </div>
              <div>
                <Label className="text-xs">CPF</Label>
                <div className="relative">
                  <Input
                    value={cpf}
                    onChange={e => {
                      setCpf(e.target.value);
                      updateSearchFromField("cpf", e.target.value);
                    }}
                    onFocus={() => {
                      if (cpf.trim().length >= 3) {
                        
                        setSearchTerm(cpf);
                        setSuggestionsOpen(true);
                      }
                    }}
                    placeholder="000.000.000-00"
                    className="h-9 text-sm pr-8"
                    autoComplete="off"
                  />
                  {isSearching && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-muted-foreground" />}
                </div>
              </div>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && (
              <div className="rounded-md border bg-popover shadow-md max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30">
                  <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
                    <Search className="h-3 w-3" />
                    {isSearching ? "Buscando..." : `${suggestions.length} visitante(s) encontrado(s)`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSuggestionsOpen(false)}
                    className="text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    Fechar
                  </button>
                </div>
                {suggestions.length === 0 && !isSearching && (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                    Nenhum visitante recorrente encontrado — preencha como novo cadastro.
                  </div>
                )}
                <ul className="divide-y">
                  {suggestions.map(s => {
                    const { rg: pRg, cpf: pCpf } = parseDocumentString(s.document);
                    const visitDate = s.created_at ? format(new Date(s.created_at), "dd/MM/yyyy") : "";
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectSuggestion(s)}
                          className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-accent transition-colors"
                        >
                          {s.photo_url ? (
                            <img
                              src={s.photo_url}
                              alt={s.name}
                              className="h-9 w-9 rounded-full object-cover shrink-0 border"
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{s.name}</span>
                              <Badge variant="outline" className="text-[9px] h-4 px-1 text-emerald-600 border-emerald-300 bg-emerald-50">
                                Recorrente
                              </Badge>
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {pCpf && <span>CPF {formatCpfMask(pCpf)}</span>}
                              {pCpf && pRg && <span> • </span>}
                              {pRg && <span>RG {pRg}</span>}
                              {!pCpf && !pRg && s.document && <span>{s.document}</span>}
                            </div>
                          </div>
                          <div className="text-[10px] text-muted-foreground shrink-0 text-right">
                            <div>{visitDate}</div>
                            {s.notes && <div className="truncate max-w-[120px]">{s.notes}</div>}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {lastVisitInfo && (
              <div className="flex items-center gap-2 p-2 rounded-md bg-accent/50 border">
                <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 text-[10px]">
                  Visitante Recorrente
                </Badge>
                <span className="text-xs text-muted-foreground">{lastVisitInfo}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Destino da Visita *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {destinations.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {destinations.map(d => {
                  const selected = destino === d.name;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDestino(selected ? null : d.name)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-xs transition-colors ${
                        selected
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Building2 className="h-5 w-5" />
                      {d.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhum destino cadastrado. Cadastre destinos na página de Destinos.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sidebar: Webcam + Validation */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Foto de Identificação</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <WebcamCapture onCapture={handleCapture} capturedUrl={photoPreview} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Validação de Acesso</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className="w-full h-11 gap-2 bg-gray-900 hover:bg-gray-800 text-white"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Confirmar Entrada
            </Button>
            <button
              type="button"
              onClick={resetForm}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancelar Registro
            </button>
          </CardContent>
        </Card>

        {/* Status bar */}
        <div className="flex items-center justify-between text-[10px] text-muted-foreground px-1">
          <div className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Conectado
          </div>
          <span>{protocol}</span>
          <span>{format(new Date(), "HH:mm:ss")}</span>
        </div>
      </div>
    </div>
  );
};

export default NovoRegistroAcesso;
